import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Clock, Flame, Target, Calendar, TrendingUp,
  Loader2, BookOpen, Calculator, FlaskConical, Pencil, Heart
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const TimeStats = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const [child, setChild] = useState(null);
  const [timeStats, setTimeStats] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const [childRes, timeRes, progressRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/time/stats/${childId}`),
        api.get(`/children/${childId}/progress`)
      ]);
      setChild(childRes.data);
      setTimeStats(timeRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const subjectIcons = {
    'alphabets': BookOpen,
    'counting': Calculator,
    'reading': BookOpen,
    'writing': Pencil,
    'math': Calculator,
    'science': FlaskConical,
    'christian': Heart,
  };

  const subjectColors = {
    'alphabets': '#4FACFE',
    'counting': '#F09819',
    'reading': '#22C55E',
    'writing': '#6366F1',
    'math': '#FF5858',
    'science': '#8B5CF6',
    'christian': '#EC4899',
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white">
        <Loader2 className="w-12 h-12 animate-spin text-green-500" />
      </div>
    );
  }

  const weeklyProgress = timeStats?.weekly_goal ? 
    Math.min(100, (timeStats.week_time / timeStats.weekly_goal) * 100) : 0;

  // Create chart data from time_by_subject
  const chartData = Object.entries(progress?.time_by_subject || {}).map(([subject, time]) => ({
    name: subject.charAt(0).toUpperCase() + subject.slice(1),
    minutes: Math.round(time / 60),
    color: subjectColors[subject] || '#94A3B8'
  }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-green-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to={`/learn/${childId}`}>
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Clock className="w-6 h-6 text-green-500" />
              <h1 className="font-fredoka font-bold text-xl text-slate-800">Time Stats</h1>
            </div>
            <div className="w-10" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Weekly Goal Progress */}
        <Card className="mb-6 bg-gradient-to-r from-green-500 to-emerald-500 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-fredoka text-2xl font-bold">Weekly Goal</h2>
                <p className="text-green-100">
                  {formatTime(timeStats?.week_time)} / {formatTime(timeStats?.weekly_goal)}
                </p>
              </div>
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                <Target className="w-10 h-10" />
              </div>
            </div>
            <Progress value={weeklyProgress} className="h-4 bg-green-400" />
            <p className="text-sm text-green-100 mt-2">
              {weeklyProgress >= 100 ? "Goal achieved! Great job!" : `${Math.round(100 - weeklyProgress)}% to go!`}
            </p>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{formatTime(timeStats?.today_time)}</p>
              <p className="text-sm text-slate-500">Today</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Calendar className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{formatTime(timeStats?.week_time)}</p>
              <p className="text-sm text-slate-500">This Week</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{formatTime(timeStats?.total_time)}</p>
              <p className="text-sm text-slate-500">All Time</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Flame className="w-8 h-8 text-orange-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{timeStats?.daily_streak || 0}</p>
              <p className="text-sm text-slate-500">Day Streak</p>
            </CardContent>
          </Card>
        </div>

        {/* Time by Subject */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Time by Subject</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }} />
                    <Tooltip 
                      formatter={(value) => [`${value} min`, 'Time']}
                      contentStyle={{ borderRadius: '8px' }}
                    />
                    <Bar 
                      dataKey="minutes" 
                      fill="#4FACFE"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No time tracked yet. Start learning!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subject Breakdown */}
        <h3 className="font-bold text-lg text-slate-800 mb-4">Subject Breakdown</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(progress?.time_by_subject || {}).map(([subject, time], index) => {
            const IconComponent = subjectIcons[subject] || BookOpen;
            const color = subjectColors[subject] || '#94A3B8';
            
            return (
              <motion.div
                key={subject}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${color}20` }}
                    >
                      <IconComponent className="w-6 h-6" style={{ color }} />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800 capitalize">{subject}</p>
                      <p className="text-sm text-slate-500">{formatTime(time)}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* No data state */}
        {(!progress?.time_by_subject || Object.keys(progress.time_by_subject).length === 0) && (
          <Card className="text-center py-12">
            <CardContent>
              <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-bold text-xl text-slate-700 mb-2">No Activity Yet</h3>
              <p className="text-slate-500 mb-4">Start learning to track your time!</p>
              <Link to={`/learn/${childId}`}>
                <Button className="bg-green-500 hover:bg-green-600">
                  Start Learning
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default TimeStats;
